import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import { Link } from 'react-router-dom/dist';

const CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .bc-root {
    background: #f5f5f7;
    font-family: -apple-system, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    color: #1d1d1f;
  }

  /* ── SCROLL PROGRESS — bottom ── */
  .bc-prog {
    position: fixed; bottom: 0; left: 0; right: 0;
    height: 3px; z-index: 9000;
    background: rgba(59,130,246,0.12);
  }
  .bc-prog-fill {
    height: 100%;
    background: linear-gradient(to right, #2563eb, #60a5fa);
    transition: width 0.1s linear;
  }

  /* ── BREADCRUMB ROW ── */
  .bc-breadcrumb {
    max-width: 1400px;
    margin: 0 auto;
    padding: 2rem clamp(1rem, 5vw, 3rem) 0;
    display: flex; align-items: center; gap: 0.6rem;
    font-size: 9px; letter-spacing: 0.16em; text-transform: uppercase;
  }
  .bc-breadcrumb a {
    color: rgba(29,29,31,0.36); text-decoration: none;
    display: inline-flex; align-items: center; gap: 5px;
    transition: color 0.2s;
  }
  .bc-breadcrumb a:hover { color: #2563eb; }
  .bc-breadcrumb a svg { width: 10px; height: 10px; }
  .bc-breadcrumb-sep {
    color: rgba(29,29,31,0.18); font-size: 10px;
  }
  .bc-breadcrumb-current {
    color: rgba(29,29,31,0.50);
    max-width: 220px;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  /* ── MASTHEAD — full-width hero with text overlaid ── */
  .bc-masthead {
    max-width: 1400px;
    margin: 0 auto;
    padding: 2rem clamp(1rem, 5vw, 3rem) 0;
  }
  .bc-mast-inner {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
    min-height: clamp(220px, 34vw, 380px);
    display: flex;
    align-items: flex-end;
    cursor: zoom-in;
  }

  /* background image fills the whole card */
  .bc-mast-bg-img {
    position: absolute; inset: 0;
    width: 100%; height: 100%;
    object-fit: cover;
    filter: brightness(0.46) saturate(0.78);
    transform: scale(1.03);
    transition: transform 8s ease, filter 0.4s ease;
    display: block;
  }
  .bc-mast-inner:hover .bc-mast-bg-img {
    transform: scale(1.0);
    filter: brightness(0.36) saturate(0.82);
  }

  /* gradient: dark bottom for legibility, slight top darkening */
  .bc-mast-vignette {
    position: absolute; inset: 0;
    background:
      linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.30) 45%, rgba(0,0,0,0.08) 75%, transparent 100%),
      linear-gradient(to right, rgba(0,0,0,0.18) 0%, transparent 55%);
    pointer-events: none; z-index: 1;
  }

  /* expand button */
  .bc-mast-img-expand {
    position: absolute; top: 14px; right: 14px; z-index: 3;
    width: 36px; height: 36px; border-radius: 50%;
    border: 0.5px solid rgba(255,255,255,0.16);
    background: rgba(0,0,0,0.38);
    color: rgba(255,255,255,0.65);
    display: flex; align-items: center; justify-content: center;
    backdrop-filter: blur(12px);
    opacity: 0; transform: scale(0.8);
    transition: all 0.22s ease;
    pointer-events: none;
    cursor: pointer;
  }
  .bc-mast-inner:hover .bc-mast-img-expand {
    opacity: 1; transform: scale(1); pointer-events: all;
  }
  .bc-mast-img-expand:hover {
    background: rgba(59,130,246,0.4); border-color: rgba(59,130,246,0.6);
  }
  .bc-mast-img-expand svg { width: 14px; height: 14px; }

  .bc-mast-left {
    position: relative; z-index: 2;
    padding: clamp(1.25rem, 3vw, 2rem) clamp(1.25rem, 3vw, 2.2rem);
    width: 100%;
  }
  .bc-mast-eye {
    font-size: 8px; letter-spacing: 0.28em; text-transform: uppercase;
    color: #60a5fa; display: flex; align-items: center; gap: 7px;
    margin-bottom: 0.7rem;
  }
  .bc-mast-eye::before {
    content: ''; width: 20px; height: 1.5px; background: #3b82f6; display: block;
  }
  .bc-mast-title {
    font-size: clamp(1.5rem, 3.5vw, 2.8rem);
    font-weight: 300; color: #fff;
    line-height: 1.08; letter-spacing: -0.025em;
    max-width: 820px;
  }
  .bc-mast-title em { font-style: italic; color: #60a5fa; }
  .bc-mast-meta {
    display: flex; align-items: center; flex-wrap: wrap;
    gap: 0.65rem; margin-top: 1rem;
  }
  .bc-mast-date {
    font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase;
    color: rgba(255,255,255,0.36);
  }
  .bc-mast-sep {
    width: 3px; height: 3px; border-radius: 50%;
    background: rgba(255,255,255,0.22);
  }
  .bc-mast-pill {
    font-size: 8px; letter-spacing: 0.14em; text-transform: uppercase;
    color: #60a5fa; border: 0.5px solid rgba(96,165,250,0.28);
    background: rgba(0,0,0,0.28);
    padding: 3px 8px; border-radius: 3px;
    backdrop-filter: blur(8px);
  }

  /* ── DIVIDER ── */
  .bc-divider {
    max-width: 1400px;
    margin: 3rem auto 0;
    padding: 0 clamp(1rem, 5vw, 3rem);
  }
  .bc-divider-line {
    height: 0.5px;
    background: rgba(29,29,31,0.10);
  }

  /* ── BODY LAYOUT ── */
  .bc-body {
    max-width: 1400px;
    margin: 0 auto;
    padding: 3.5rem clamp(1rem, 5vw, 3rem) 7rem;
    display: grid;
    grid-template-columns: 200px 1fr 160px;
    gap: 3.5rem;
    align-items: start;
  }
  @media (max-width: 1000px) {
    .bc-body {
      grid-template-columns: 1fr;
      gap: 0;
    }
    .bc-toc, .bc-side-right { display: none; }
  }

  /* ── LEFT TOC ── */
  .bc-toc {
    position: sticky; top: 2rem;
    display: flex; flex-direction: column; gap: 0;
  }
  .bc-toc-label {
    font-size: 8px; letter-spacing: 0.22em; text-transform: uppercase;
    color: rgba(29,29,31,0.28); margin-bottom: 0.9rem;
  }
  .bc-toc-link {
    display: block;
    font-size: 0.76rem; line-height: 1.45; color: rgba(29,29,31,0.36);
    text-decoration: none;
    padding: 0.28rem 0 0.28rem 0.85rem;
    border-left: 2px solid rgba(29,29,31,0.07);
    transition: color 0.2s, border-color 0.2s;
  }
  .bc-toc-link:hover { color: #2563eb; }
  .bc-toc-link.on {
    color: #2563eb; border-left-color: #3b82f6; font-weight: 500;
  }
  .bc-toc-link.h2 { padding-left: 1.4rem; }
  .bc-toc-divider {
    margin: 1.5rem 0; height: 0.5px;
    background: rgba(29,29,31,0.07);
  }
  .bc-toc-stat-label {
    font-size: 7px; letter-spacing: 0.2em; text-transform: uppercase;
    color: rgba(29,29,31,0.24); margin-bottom: 0.45rem;
  }
  .bc-toc-track {
    height: 2px; background: rgba(29,29,31,0.08);
    border-radius: 2px; overflow: hidden;
  }
  .bc-toc-fill {
    height: 100%; background: #3b82f6;
    border-radius: 2px; transition: width 0.15s linear;
  }
  .bc-toc-pct { font-size: 10px; color: #3b82f6; margin-top: 0.38rem; font-weight: 500; }
  .bc-toc-eta { font-size: 8px; color: rgba(29,29,31,0.26); margin-top: 0.2rem; }

  /* ── PROSE ── */
  .bc-prose {
    color: #3a3a3c;
    font-size: clamp(0.96rem, 1.5vw, 1.08rem);
    line-height: 1.84; min-width: 0;
  }
  .bc-prose > p:first-of-type {
    font-size: clamp(1.06rem, 1.8vw, 1.24rem);
    line-height: 1.72; color: #2c2c2e; margin-bottom: 1.8rem;
    padding-bottom: 1.8rem;
    border-bottom: 0.5px solid rgba(29,29,31,0.08);
  }
  .bc-prose > p:first-of-type::first-letter {
    float: left; font-size: 4.6rem; line-height: 0.76;
    padding-right: 12px; padding-top: 8px;
    font-weight: 700; letter-spacing: -0.05em; color: #1d1d1f;
  }
  .bc-prose p { margin-bottom: 1.5rem; }
  .bc-prose p:last-child { margin-bottom: 0; }
  .bc-prose h1, .bc-prose h2 {
    font-size: clamp(1.15rem, 1.9vw, 1.48rem);
    font-weight: 400; color: #1d1d1f;
    letter-spacing: -0.02em; line-height: 1.18;
    margin: 3rem 0 0.9rem;
    padding-bottom: 0.6rem;
    border-bottom: 0.5px solid rgba(29,29,31,0.09);
    scroll-margin-top: 2rem;
    display: flex; align-items: center; gap: 0.75rem;
  }
  .bc-prose h1::before, .bc-prose h2::before {
    content: ''; display: block; flex-shrink: 0;
    width: 20px; height: 1.5px; background: #3b82f6;
  }
  .bc-prose h3 {
    font-size: 1rem; font-weight: 600; color: #1d1d1f;
    margin: 2.2rem 0 0.5rem; letter-spacing: -0.01em;
    scroll-margin-top: 2rem;
  }
  .bc-prose h4, .bc-prose h5, .bc-prose h6 {
    font-size: 0.82rem; font-weight: 600;
    color: rgba(29,29,31,0.52); text-transform: uppercase;
    letter-spacing: 0.10em; margin: 1.8rem 0 0.45rem;
  }
  .bc-prose strong { color: #1d1d1f; font-weight: 600; }
  .bc-prose em { font-style: italic; }
  .bc-prose a { color: #2563eb; text-underline-offset: 3px; }
  .bc-prose a:hover { color: #1d4ed8; }
  .bc-prose blockquote {
    margin: 2.5rem 0;
    padding: 1.4rem 1.6rem;
    background: #fff;
    border-radius: 14px;
    border: 0.5px solid rgba(29,29,31,0.07);
    border-left: 3px solid #3b82f6;
    position: relative;
    box-shadow: 0 2px 12px rgba(0,0,0,0.04);
  }
  .bc-prose blockquote p {
    font-size: 1.06rem; font-style: italic;
    color: #2c2c2e; line-height: 1.7; margin: 0; border: none; padding: 0;
  }
  .bc-prose ul, .bc-prose ol { padding-left: 1.5rem; margin-bottom: 1.5rem; }
  .bc-prose li { margin-bottom: 0.42rem; }
  .bc-prose code {
    background: rgba(29,29,31,0.07); padding: 2px 6px;
    border-radius: 4px; font-size: 0.83em; color: #1d1d1f;
  }
  .bc-prose pre {
    background: #1d1d1f; color: #e5e5ea;
    padding: 1.25rem 1.5rem; border-radius: 14px;
    overflow-x: auto; font-size: 0.82rem; margin-bottom: 1.5rem;
  }
  .bc-prose pre code { background: none; padding: 0; color: inherit; }
  .bc-prose img {
    width: 100%; border-radius: 14px;
    margin: 2rem 0; display: block;
    cursor: zoom-in;
    transition: transform 0.3s ease, filter 0.3s ease;
    box-shadow: 0 4px 28px rgba(0,0,0,0.10);
  }
  .bc-prose img:hover { transform: scale(1.008); filter: brightness(0.92); }
  .bc-prose hr { border: none; border-top: 0.5px solid rgba(29,29,31,0.09); margin: 2.5rem 0; }

  /* ── RIGHT SIDE PANEL ── */
  .bc-side-right {
    position: sticky; top: 2rem;
    display: flex; flex-direction: column; gap: 1.25rem;
  }
  .bc-side-card {
    background: #fff;
    border: 0.5px solid rgba(29,29,31,0.08);
    border-radius: 12px;
    padding: 1.1rem 1.2rem;
  }
  .bc-side-card-label {
    font-size: 8px; letter-spacing: 0.2em; text-transform: uppercase;
    color: rgba(29,29,31,0.28); margin-bottom: 0.6rem;
  }
  .bc-side-card-val {
    font-size: 1.4rem; font-weight: 300;
    color: #1d1d1f; letter-spacing: -0.02em; line-height: 1;
  }
  .bc-side-card-sub {
    font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase;
    color: rgba(29,29,31,0.28); margin-top: 0.3rem;
  }
  .bc-side-card-val.blue { color: #2563eb; }

  /* ── ARTICLE FOOTER ── */
  .bc-footer {
    max-width: 1400px;
    margin: 0 auto;
    padding: 2rem clamp(1rem, 5vw, 3rem) 6rem;
    border-top: 0.5px solid rgba(29,29,31,0.09);
    display: flex; align-items: center;
    justify-content: space-between; flex-wrap: wrap; gap: 1rem;
  }
  .bc-footer-back {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase;
    color: #2563eb; text-decoration: none;
    border: 0.5px solid rgba(59,130,246,0.28);
    background: rgba(59,130,246,0.05);
    padding: 10px 20px; border-radius: 5px;
    transition: all 0.22s ease;
  }
  .bc-footer-back:hover { background: #2563eb; color: #fff; border-color: #2563eb; }
  .bc-footer-back svg { width: 11px; height: 11px; }
  .bc-footer-date {
    font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase;
    color: rgba(29,29,31,0.24);
  }

  /* ── VIEWER ── */
  .bc-viewer {
    position: fixed; inset: 0; z-index: 9999;
    display: flex; align-items: center; justify-content: center;
    padding: clamp(0.75rem, 3vw, 2rem);
    animation: bc-fi 0.22s ease;
  }
  .bc-vbg {
    position: absolute; inset: 0;
    background: rgba(0,0,0,0.40);
    backdrop-filter: blur(30px) saturate(1.5) brightness(0.72);
    -webkit-backdrop-filter: blur(30px) saturate(1.5) brightness(0.72);
    cursor: pointer;
  }
  .bc-vglass {
    position: relative; z-index: 1;
    width: 100%; max-width: 1020px; max-height: 94vh;
    display: flex; flex-direction: column;
    border-radius: 20px; overflow: hidden;
    background: rgba(20,20,24,0.82);
    border: 0.5px solid rgba(255,255,255,0.08);
    box-shadow: 0 40px 120px rgba(0,0,0,0.72), 0 6px 18px rgba(0,0,0,0.32);
    backdrop-filter: blur(80px) saturate(2.4) brightness(0.88);
    -webkit-backdrop-filter: blur(80px) saturate(2.4) brightness(0.88);
    animation: bc-su 0.32s cubic-bezier(0.16,1,0.3,1);
  }
  .bc-varea {
    flex: 1; min-height: 0; background: #000;
    display: flex; align-items: center; justify-content: center;
    overflow: hidden; position: relative;
    cursor: zoom-in; max-height: 78vh;
  }
  .bc-varea.zoomed { cursor: grab; }
  .bc-varea.drag   { cursor: grabbing; }
  .bc-varea img {
    max-width: 100%; max-height: 78vh; width: auto; height: auto;
    object-fit: contain; display: block;
    transition: transform 0.24s cubic-bezier(0.25,0.46,0.45,0.94);
    transform-origin: center center;
    user-select: none; -webkit-user-drag: none;
  }
  .bc-vhint {
    position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%);
    font-size: 9px; letter-spacing: 0.09em; text-transform: uppercase;
    color: rgba(255,255,255,0.18);
    background: rgba(0,0,0,0.30); padding: 3px 10px; border-radius: 20px;
    pointer-events: none; white-space: nowrap;
  }
  .bc-vclose {
    position: absolute; top: 11px; right: 11px;
    width: 28px; height: 28px; border-radius: 50%;
    border: 0.5px solid rgba(255,255,255,0.10);
    background: rgba(48,48,54,0.70); color: rgba(255,255,255,0.62);
    font-size: 12px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    z-index: 12; transition: all 0.18s; line-height: 1;
  }
  .bc-vclose:hover { background: rgba(190,42,42,0.48); color: #fff; }
  .bc-vfoot {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.85rem 1.3rem;
    border-top: 0.5px solid rgba(255,255,255,0.06);
    background: rgba(10,10,12,0.50); flex-shrink: 0; gap: 1rem;
  }
  .bc-vt { font-size: 0.88rem; color: rgba(255,255,255,0.76); }
  .bc-vzbar { display: flex; align-items: center; gap: 6px; }
  .bc-vzb {
    width: 28px; height: 28px; border-radius: 50%;
    border: 0.5px solid rgba(255,255,255,0.10);
    background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.60);
    font-size: 14px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.18s; line-height: 1;
  }
  .bc-vzb:hover { background: rgba(59,130,246,0.24); color: #60a5fa; }
  .bc-vzpct { font-size: 9px; color: rgba(255,255,255,0.28); letter-spacing: 0.06em; min-width: 32px; text-align: center; }

  @keyframes bc-fi  { from { opacity: 0; } to { opacity: 1; } }
  @keyframes bc-su  {
    from { opacity: 0; transform: translateY(18px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 640px) {
    .bc-mast-title { font-size: clamp(1.7rem, 7vw, 2.4rem); }
  }
`;

/* icons */
const BackArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>
  </svg>
);
const ExpandIco = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
  </svg>
);

/* helpers */
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
const countWords = (s = '') => s.trim().split(/\s+/).filter(Boolean).length;
const readMins   = (s = '') => Math.max(1, Math.round(countWords(s) / 200));

/* TOC */
function parseTOC(md = '') {
  return md.split('\n')
    .reduce((acc, line) => {
      const m = line.match(/^(#{1,2})\s+(.+)/);
      if (m) acc.push({ text: m[2].trim(), id: m[2].trim().toLowerCase().replace(/[^a-z0-9]+/g, '-'), level: m[1].length });
      return acc;
    }, []);
}

/* Progress */
const Prog = ({ pct }) => (
  <div className="bc-prog"><div className="bc-prog-fill" style={{ width: `${pct}%` }} /></div>
);

/* Viewer */
const Viewer = ({ src, title, onClose }) => {
  const [scale, setScale]   = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [drag, setDrag]     = useState(false);
  const ds = useRef(null), ir = useRef(null);

  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const cl  = (sc, ox, oy) => { const el = ir.current; if (!el) return { x: ox, y: oy }; const mx = Math.max(0, (el.offsetWidth*(sc-1))/2), my = Math.max(0, (el.offsetHeight*(sc-1))/2); return { x: Math.min(mx, Math.max(-mx, ox)), y: Math.min(my, Math.max(-my, oy)) }; };
  const zm  = (d) => setScale(p => { const n = Math.min(5, Math.max(1, p+d)); if (n===1) setOffset({x:0,y:0}); return n; });
  const rst = () => { setScale(1); setOffset({x:0,y:0}); };
  const onW = (e) => { e.preventDefault(); zm(e.deltaY < 0 ? 0.5 : -0.5); };
  const onD = (e) => { if (scale<=1) return; e.preventDefault(); setDrag(true); ds.current={mx:e.clientX,my:e.clientY,ox:offset.x,oy:offset.y}; };
  const onM = (e) => { if (!drag||!ds.current) return; setOffset(cl(scale, ds.current.ox+e.clientX-ds.current.mx, ds.current.oy+e.clientY-ds.current.my)); };
  const onU = () => setDrag(false);
  const onC = () => { if (scale<=1) zm(1.5); else rst(); };

  return (
    <div className="bc-viewer">
      <div className="bc-vbg" onClick={onClose}/>
      <div className="bc-vglass">
        <button className="bc-vclose" onClick={onClose}>✕</button>
        <div className={`bc-varea${scale>1?' zoomed':''}${drag?' drag':''}`}
          onWheel={onW} onMouseDown={onD} onMouseMove={onM} onMouseUp={onU} onMouseLeave={onU}
          onClick={scale<=1?onC:undefined}>
          <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',overflow:'hidden'}}>
            <img ref={ir} src={src} alt={title}
              style={{ transform:`scale(${scale}) translate(${offset.x/scale}px,${offset.y/scale}px)`, transition:drag?'none':undefined }}
              draggable={false}/>
          </div>
          {scale<=1 && <div className="bc-vhint">scroll or click to zoom</div>}
        </div>
        <div className="bc-vfoot">
          <span className="bc-vt">{title}</span>
          <div className="bc-vzbar">
            <button className="bc-vzb" onClick={()=>zm(-0.5)}>−</button>
            <span className="bc-vzpct">{Math.round(scale*100)}%</span>
            <button className="bc-vzb" onClick={()=>zm(0.5)}>+</button>
            {scale>1 && <button className="bc-vzb" style={{fontSize:9,width:'auto',padding:'0 8px',borderRadius:4,letterSpacing:'0.05em'}} onClick={rst}>Reset</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

/* Main */
const BlogContent = ({ blogs }) => {
  const { id } = useParams();
  const [pct,    setPct]    = useState(0);
  const [viewer, setViewer] = useState(null);
  const [activeH,setActiveH]= useState('');

  let blog = {};
  if (blogs) { const arr = blogs.filter(b => b.id === id); blog = arr[0] || {}; }

  const words = countWords(blog.content);
  const mins  = readMins(blog.content);
  const toc   = useMemo(() => parseTOC(blog.content), [blog.content]);

  /* scroll progress */
  useEffect(() => {
    const fn = () => {
      const el = document.documentElement;
      const t  = el.scrollHeight - el.clientHeight;
      setPct(t > 0 ? Math.min(100, (el.scrollTop / t) * 100) : 0);
    };
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* active heading */
  useEffect(() => {
    if (!toc.length) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveH(e.target.id); }),
      { rootMargin: '-15% 0px -75% 0px' }
    );
    toc.forEach(({ id }) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [toc]);

  /* title split */
  const titleWords = (blog.title || '').split(' ');
  const half = Math.ceil(titleWords.length / 2);
  const t1 = titleWords.slice(0, half).join(' ');
  const t2 = titleWords.slice(half).join(' ');

  const timeLeft = Math.max(1, Math.round(mins * (1 - pct / 100)));

  /* renderers */
  const components = useMemo(() => ({
    img({ src, alt }) {
      return (
        <img src={src} alt={alt}
          onClick={() => setViewer({ src, title: alt || blog.title })}
          title="Click to view full size"
        />
      );
    },
    h1({ children }) { const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-'); return <h1 id={id}>{children}</h1>; },
    h2({ children }) { const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-'); return <h2 id={id}>{children}</h2>; },
    h3({ children }) { const id = String(children).toLowerCase().replace(/[^a-z0-9]+/g, '-'); return <h3 id={id}>{children}</h3>; },
  }), [blog.title]);

  return (
    <>
      <style>{CSS}</style>
      <Prog pct={pct} />
      {viewer && <Viewer src={viewer.src} title={viewer.title} onClose={() => setViewer(null)} />}

      <div className="bc-root">

        {/* BREADCRUMB */}
        <div className="bc-breadcrumb">
          <Link to="/Blog"><BackArrow /> All Articles</Link>
          <span className="bc-breadcrumb-sep">/</span>
          <span className="bc-breadcrumb-current">{blog.title}</span>
        </div>

        {/* MASTHEAD — text overlaid on full-width image */}
        <div className="bc-masthead">
          <div
            className="bc-mast-inner"
            onClick={() => blog.thumbnail && setViewer({ src: blog.thumbnail, title: blog.title })}
          >
            {/* background image */}
            {blog.thumbnail && (
              <img className="bc-mast-bg-img" src={blog.thumbnail} alt={blog.title} />
            )}
            {!blog.thumbnail && (
              <div style={{ position:'absolute', inset:0, background:'#1a1a1e' }} />
            )}

            {/* vignette */}
            <div className="bc-mast-vignette" />

            {/* expand icon */}
            {blog.thumbnail && (
              <div className="bc-mast-img-expand"
                onClick={e => { e.stopPropagation(); setViewer({ src: blog.thumbnail, title: blog.title }); }}>
                <ExpandIco />
              </div>
            )}

            {/* text on top */}
            <div className="bc-mast-left">
              <p className="bc-mast-eye">Kingdom News</p>
              <h1 className="bc-mast-title">
                {t1}{t2 ? <> <em>{t2}</em></> : null}
              </h1>
              <div className="bc-mast-meta">
                {blog.createdAt && <span className="bc-mast-date">{fmt(blog.createdAt)}</span>}
                <span className="bc-mast-sep"/>
                <span className="bc-mast-pill">{mins} min read</span>
                <span className="bc-mast-pill">{words.toLocaleString()} words</span>
              </div>
            </div>

          </div>
        </div>

        {/* DIVIDER */}
        <div className="bc-divider"><div className="bc-divider-line"/></div>

        {/* BODY: TOC | PROSE | STATS */}
        <div className="bc-body">

          {/* left TOC */}
          <aside className="bc-toc">
            {toc.length > 0 && (
              <>
                <p className="bc-toc-label">Contents</p>
                {toc.map(({ text, id, level }) => (
                  <a key={id} href={`#${id}`}
                    className={`bc-toc-link${level===2?' h2':''}${activeH===id?' on':''}`}
                    onClick={e => { e.preventDefault(); document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); }}>
                    {text}
                  </a>
                ))}
                <div className="bc-toc-divider"/>
              </>
            )}
            <p className="bc-toc-stat-label">Progress</p>
            <div className="bc-toc-track">
              <div className="bc-toc-fill" style={{ width: `${pct}%` }}/>
            </div>
            <p className="bc-toc-pct">{Math.round(pct)}%</p>
            <p className="bc-toc-eta">~{timeLeft} min left</p>
          </aside>

          {/* prose */}
          <div className="bc-prose">
            <ReactMarkdown className="line-break" components={components}>
              {blog.content}
            </ReactMarkdown>
          </div>

          {/* right stats */}
          <aside className="bc-side-right">
            <div className="bc-side-card">
              <p className="bc-side-card-label">Read time</p>
              <p className="bc-side-card-val blue">{mins}</p>
              <p className="bc-side-card-sub">minutes</p>
            </div>
            <div className="bc-side-card">
              <p className="bc-side-card-label">Words</p>
              <p className="bc-side-card-val">{words.toLocaleString()}</p>
              <p className="bc-side-card-sub">total</p>
            </div>
            {blog.createdAt && (
              <div className="bc-side-card">
                <p className="bc-side-card-label">Published</p>
                <p className="bc-side-card-val" style={{ fontSize: '0.82rem', lineHeight: 1.3 }}>{fmt(blog.createdAt)}</p>
              </div>
            )}
          </aside>

        </div>

        {/* FOOTER */}
        <div className="bc-footer">
          <Link to="/Blog" className="bc-footer-back"><BackArrow /> All Articles</Link>
          {blog.createdAt && <span className="bc-footer-date">Published {fmt(blog.createdAt)}</span>}
        </div>

      </div>
    </>
  );
};

export default BlogContent;